/**
 * Google reCAPTCHA Enterprise integration (score-based, invisible).
 *
 * The module lazy-loads the Google reCAPTCHA Enterprise script and hands out
 * fresh, single-use tokens to the axios request interceptor in {@link ./Api.ts}.
 *
 * Design notes:
 * - Module-level state (site key, protected-path patterns, readyPromise) is
 *   used so the axios interceptor can obtain a token without pulling values
 *   out of React context on every request.
 * - Tokens are only issued for URLs that target `/api/{version}/openserviceproxy/`
 *   AND whose path suffix matches one of the CMS-published patterns in
 *   `FrontendConfigurationModel.reCaptchaProtectedPaths`. This mirrors the
 *   server-side `ReCaptchaProtectedPathsFilter`. The list is never hardcoded
 *   here — the CMS is the single source of truth.
 * - Enterprise score-based tokens are single-use and expire after ~2 minutes,
 *   so a fresh token is generated per request rather than cached at startup.
 * - `getRecaptchaTokenForUrl` fails CLOSED: if an open-service-proxy request
 *   is issued before {@link configureRecaptcha} has ever run, it throws. This
 *   prevents a protected endpoint from being reached without a token during
 *   the CMS-loading race.
 * - Other failures (script load error, `grecaptcha.enterprise.execute` throws)
 *   return `undefined` and the request proceeds without the header; the
 *   backend decides whether to reject unauthenticated calls.
 *
 * @see https://cloud.google.com/recaptcha/docs/using-features
 */

import { isProtectedPath } from '../utilities/isProtectedPath';

declare global {
	interface Window {
		grecaptcha?: {
			enterprise?: {
				ready: (cb: () => void) => void;
				execute: (siteKey: string, options: { action: string }) => Promise<string>;
			};
		};
	}
}

/** Id of the `<script>` element injected into `<head>`; used to avoid duplicates. */
const RECAPTCHA_SCRIPT_ID = 'recaptcha-enterprise-script';

/** HTTP header attached to open-proxy requests. Read by the backend for `siteverify`. */
export const RECAPTCHA_HEADER = 'X-Recaptcha-Token';

/**
 * Anchors path matching to the canonical open-service-proxy segment. Any URL
 * containing `/api/{version}/openserviceproxy/` is considered an open-proxy
 * request; the captured group is the path suffix used for pattern matching
 * (mirrors the server-side extraction rule).
 */
const OPEN_PROXY_PATH_REGEX = /\/api\/[^/]+\/openserviceproxy\/([^?#]*)/i;

let siteKey: string | undefined;
let protectedPathPatterns: readonly string[] = [];
let readyPromise: Promise<void> | undefined;
/**
 * `true` once {@link configureRecaptcha} has run at least once, even with
 * undefined arguments (which is how the CMS communicates "reCAPTCHA disabled").
 * Guards the fail-closed branch in {@link getRecaptchaTokenForUrl}: an
 * open-proxy request issued before CMS load throws instead of silently
 * skipping the token.
 */
let cmsConfigLoaded = false;
/**
 * Guards the one-time `[recaptcha] disabled: ...` warning so the log only fires
 * on transitions into the disabled state, not on every React render (App calls
 * `configureRecaptcha` in its render body).
 */
let disabledLogged = false;

/**
 * Configure reCAPTCHA Enterprise and lazy-load the Google script.
 *
 * Called from {@link ../App.tsx} once the Optimizely page model is available.
 * `key` must be truthy for reCAPTCHA to activate — an empty site key from the
 * CMS disables the feature entirely. The first call (even with undefined
 * arguments) marks the CMS configuration as loaded, unblocking the
 * fail-closed check in {@link getRecaptchaTokenForUrl}.
 *
 * Idempotent: repeated calls with the same site key only refresh the pattern
 * list; a different site key triggers a script re-load.
 *
 * @param key             reCAPTCHA Enterprise site key from
 *                        `FrontendConfigurationModel.recaptchaSiteKey`.
 * @param protectedPaths  Path patterns from
 *                        `FrontendConfigurationModel.reCaptchaProtectedPaths`.
 *                        Compared against the URI suffix after
 *                        `/api/{version}/openserviceproxy/` using
 *                        {@link isProtectedPath}.
 */
export const configureRecaptcha = (key: string | undefined, protectedPaths: readonly string[] | undefined): void => {
	cmsConfigLoaded = true;
	protectedPathPatterns = protectedPaths ?? [];

	if (!key) {
		if (!disabledLogged) {
			disabledLogged = true;
			// Highest-signal log in the whole module: if the CMS forgets to publish
			// `recaptchaSiteKey` this line makes it obvious in DevTools without having
			// to inspect Redux/context or Network tab.
			console.warn('[recaptcha] disabled: siteKey missing');
		}
		return;
	}
	disabledLogged = false;

	if (siteKey === key && readyPromise !== undefined) {
		return;
	}
	siteKey = key;
	readyPromise = loadScript(key);
	readyPromise
		.then(() => console.info(`[recaptcha] enabled (protectedPaths=${protectedPathPatterns.length})`))
		.catch((err) => console.error('[recaptcha] script load rejected', err));
};

/**
 * Inject `https://www.google.com/recaptcha/enterprise.js?render=<siteKey>` into
 * `<head>` and return a promise that resolves once
 * `grecaptcha.enterprise.ready(...)` fires.
 *
 * If a script with {@link RECAPTCHA_SCRIPT_ID} is already present the promise
 * hooks into that element instead of appending a duplicate.
 */
const loadScript = (key: string): Promise<void> =>
	new Promise((resolve, reject) => {
		if (typeof document === 'undefined') {
			return reject(new Error('document not available'));
		}
		const existing = document.getElementById(RECAPTCHA_SCRIPT_ID) as HTMLScriptElement | null;
		if (existing) {
			if (window.grecaptcha?.enterprise?.execute) {
				window.grecaptcha.enterprise.ready(() => resolve());
			} else {
				existing.addEventListener('load', () => {
					window.grecaptcha?.enterprise?.ready(() => resolve());
				});
				existing.addEventListener('error', () => {
					reject(new Error('recaptcha script failed to load'));
				});
			}
			return;
		}
		const script = document.createElement('script');
		script.id = RECAPTCHA_SCRIPT_ID;
		script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(key)}`;
		script.async = true;
		script.defer = true;
		script.onload = () => {
			window.grecaptcha?.enterprise?.ready(() => resolve());
		};
		script.onerror = () => {
			reject(new Error('recaptcha script failed to load'));
		};
		document.head.appendChild(script);
	});

/**
 * Derive a reCAPTCHA Enterprise `action` name from a request URL.
 *
 * Action names must match `[A-Za-z_/]`; anything else is replaced with `_`.
 * The last non-empty path segment is used, falling back to `api_call`.
 * The action is reported to Google and shows up in the reCAPTCHA admin console,
 * making it easier to identify traffic patterns per endpoint.
 */
const sanitizeAction = (url: string): string => {
	const path = url.split('?')[0].split('/').findLast(Boolean) ?? 'api_call';
	return path.replace(/[^A-Za-z_/]/g, '_') || 'api_call';
};

/**
 * Extract the URI suffix that follows `/api/{version}/openserviceproxy/`, or
 * `undefined` when the URL is not an open-service-proxy request.
 */
const extractOpenProxySuffix = (url: string | undefined): string | undefined => {
	if (!url) return undefined;
	const match = OPEN_PROXY_PATH_REGEX.exec(url);
	return match ? match[1] : undefined;
};

/**
 * Produce a fresh reCAPTCHA Enterprise token for the given request URL.
 *
 * @returns A single-use token string, or `undefined` when:
 *          - the URL does not target the open-service-proxy, or
 *          - the CMS has not published a site key, or
 *          - the URL suffix does not match any configured protected path, or
 *          - the Google script failed to load, or
 *          - `grecaptcha.enterprise.execute` threw.
 *
 * @throws {Error} when an open-service-proxy URL is called BEFORE
 *                 {@link configureRecaptcha} has ever run. Fails closed so a
 *                 protected endpoint cannot be reached without a token during
 *                 the CMS-loading race.
 */
export const getRecaptchaTokenForUrl = async (url: string | undefined): Promise<string | undefined> => {
	const suffix = extractOpenProxySuffix(url);
	if (suffix === undefined) {
		return undefined;
	}

	if (!cmsConfigLoaded) {
		throw new Error(
			`[recaptcha] refusing to call open-service-proxy URL "${url}" before CMS configuration has loaded`
		);
	}

	if (!isProtectedPath(suffix, protectedPathPatterns)) {
		return undefined;
	}

	if (!siteKey || readyPromise === undefined) {
		return undefined;
	}

	const action = sanitizeAction(url as string);
	try {
		await readyPromise;
		if (!window.grecaptcha?.enterprise?.execute) {
			return undefined;
		}
		return await window.grecaptcha.enterprise.execute(siteKey, { action });
	} catch (err) {
		console.error(`[recaptcha] grecaptcha.enterprise.execute failed for url=${url} action=${action}`, err);
		return undefined;
	}
};

/**
 * Reset internal module state. Intended for use in unit tests — do not call
 * from application code.
 *
 * @internal
 */
export const __resetRecaptchaForTests = (): void => {
	siteKey = undefined;
	protectedPathPatterns = [];
	readyPromise = undefined;
	cmsConfigLoaded = false;
	disabledLogged = false;
	if (typeof document !== 'undefined') {
		document.getElementById(RECAPTCHA_SCRIPT_ID)?.remove();
	}
};
